import React, { useState, useEffect } from 'react';
import { fetchPayments, savePayment, fetchCustomers, deletePayment } from '../../services/api';

const now = new Date();
const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

const formatDateForAPI = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
    // YYYY-MM-DD to DD-MM-YYYY
    const [y, m, d] = dateStr.split("-");
    const formattedDate = `${d}-${m}-${y}`;
    return formattedDate;
  }
  return dateStr;
};

const formatDateForUI = (date) => {
  if (!date) return "";
  let d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  let year = d.getFullYear();
  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;
  return [year, month, day].join('-');
};

const getTodayForUI = () => formatDateForUI(new Date());
const getFirstDayForUI = () => formatDateForUI(firstDayOfMonth);

const today = getTodayForUI();

export default function Payments({ user }) {
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [fromDate, setFromDate] = useState(getFirstDayForUI());
  const [toDate, setToDate] = useState(getTodayForUI());

  const companyId = user?.Companyid || user?.company_id || "1";

  const [form, setForm] = useState({
    date: today,
    party: '',
    amount: '',
    mode: 'Cash'
  });

  useEffect(() => {
    loadData();
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch Payments
      const pRes = await fetchPayments({
        Companyid: companyId,
        Name: "",
        FromDate: formatDateForAPI(fromDate),
        ToDate: formatDateForAPI(toDate)
      });
      if (pRes.success) {
        const raw = pRes.Response;
        const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
        setPayments(list);
      }

      // 2. Fetch Suppliers (using fetchCustomers as per system pattern)
      const suppRes = await fetchCustomers(companyId);
      if (suppRes.success) {
        setSuppliers(suppRes.Response || []);
      }

    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.party || !form.amount) {
      alert("Please fill all required fields");
      return;
    }

    const apiData = {
      Companyid: companyId,
      Name: form.party,
      Amount: form.amount.toString(),
      Date1: form.date,
      PaymentMode: form.mode,
      IDS: editId ? String(editId) : "0",
      Receiptid: editId ? String(editId) : "0"
    };

    setLoading(true);
    try {
      const res = await savePayment(apiData);
      if (res.success || res.Response === "UPDATED" || res.Response === "INSERTED") {
        alert(res.message || "Payment saved successfully!");
        loadData();
        setShowForm(false);
        setEditId(null);
        setForm({ date: today, party: '', amount: '', mode: 'Cash' });
      } else {
        alert(res.message || "Failed to save payment");
      }
    } catch (err) {
      console.error('Save error:', err);
      alert("Error saving record");
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (p) => {
    // Priority: Receiptid from object, then Recid, then IDS, then others
    const receiptId = p.Receiptid || p.Recid || p.IDS || p.Paymentid || p.id || p.Id || "0";
    setEditId(receiptId);
    setForm({
      date: p.Date1 || p.date || today,
      party: p.Customer || p.Name || p.party || p.customer || p.name || '',
      amount: p.DebitAmount || p.Debit_Amount || p.Amount || p.amount || p.Total || p.total || '',
      mode: p.PaymentMode || p.mode || p.payment_mode || 'Cash'
    });
    setShowForm(true);
  };

  const handleDelete = async (p) => {
    const receiptId = p.Receiptid || p.Recid || p.IDS || p.Paymentid || p.id || p.Id;
    if (!receiptId) return;
    if (!window.confirm("Are you sure you want to delete this payment record?")) return;

    setLoading(true);
    try {
      const result = await deletePayment(receiptId);
      if (result.success || result.Response === "DELETED" || result.status === "Success") {
        await loadData();
      } else {
        alert(result.message || "Failed to delete payment");
      }
    } catch (err) {
      console.error("Delete payment error:", err);
    } finally {
      setLoading(false);
    }
  };

  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.DebitAmount || p.Debit_Amount || p.Amount || p.amount || p.Total || p.total || 0)), 0);

  if (loading && payments.length === 0) return <div style={{padding: 20}}>Loading...</div>;

  return (
    <div className="pump-page">
      <div className="page-header">
        <h2 className="page-title">Payments</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="date-filter" style={{ display: 'flex', gap: 5, alignItems: 'center', background: '#fff', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
            <input 
              type="date" 
              className="form-control" 
              style={{ width: 130, border: 'none', padding: 0, fontSize: 13 }}
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
            <span>to</span>
            <input 
              type="date" 
              className="form-control" 
              style={{ width: 130, border: 'none', padding: 0, fontSize: 13 }}
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
            <button className="btn btn-secondary btn-sm" onClick={loadData} title="Apply Filter">
              <i className="bi bi-filter"></i>
            </button>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(true); setEditId(null); setForm({ date: today, party: '', amount: '', mode: 'Cash' }); }}>
            ＋ New Payment
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 12, marginBottom: 14 }}>
        {[
          { label: 'Total Payments', val: payments.length, color: 'var(--primary)' },
          { label: 'Total Amount Paid', val: '₹' + totalPaid.toLocaleString(), color: 'var(--secondary)' },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', boxShadow: 'var(--shadow-sm)', borderLeft: `4px solid ${c.color}` }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.val}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? 'Edit Payment' : 'New Payment'}</div>
          <div className="form-row">
            <div className="form-group"><label>Date</label><input className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group">
              <label>Party Name</label>
              <select className="form-control" value={form.party} onChange={e => setForm({ ...form, party: e.target.value })}>
                <option value="">Select Party</option>
                {suppliers.map(s => (
                  <option key={s.Customerid || s.id} value={s.Name || s.name}>{s.Name || s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group"><label>Amount (₹)</label><input type="number" className="form-control" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="form-group">
              <label>Mode</label>
              <select className="form-control" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                <option>Cash</option>
                <option>Bank Transfer</option>
                <option>UPI</option>
                <option>Cheque</option>
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleSave}>
              {editId ? "Update Payment" : "Save Payment"}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
              ✕ Cancel
            </button>
          </div>
        </div>
      )}
      
      <div className="section-card">
        <div className="section-header">Payment History — ₹{totalPaid.toLocaleString()}</div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-center">Date</th>
                <th className="text-left">Party Name</th>
                <th className="text-right">Amount (₹)</th>
                <th className="text-center">Mode</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan="5" style={{textAlign:'center', padding:20}}>No records found</td></tr>
              ) : (
                payments.map((p, i) => (
                  <tr key={i}>
                    <td className="text-center">{p.Date1 || p.date}</td>
                    <td className="text-left" style={{ fontWeight: 600 }}>{p.Customer || p.Name || p.party || p.customer || p.name || '—'}</td>
                    <td className="text-right" style={{ fontWeight: 700, color: 'var(--danger)' }}>
                      ₹{parseFloat(p.DebitAmount || p.Debit_Amount || p.Amount || p.amount || p.Total || p.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-center">
                      <span className="badge badge-outline">{p.PaymentMode || p.mode || p.payment_mode}</span>
                    </td>
                    <td className="text-center">
                      <button className="icon-btn" onClick={() => openEdit(p)} title="Edit">
                        <i className="bi bi-pencil-square"></i>
                      </button>

                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
